import { forwardRef, HTMLProps, ReactNode, Ref, useState } from "react";
import { InputMask } from "@react-input/mask";
import clsx from "clsx";

import { EyeSvg, EyeOffSvg } from "@components/index";

interface InputProps extends HTMLProps<HTMLInputElement> {
  name: string;
  type?: "text" | "password" | "number" | "phone";
  label: string;
  isSimpleLabel?: boolean;
  disabled?: boolean;
  height?: "small" | "medium";
  icon?: ReactNode;
  helpText?: string;
  error?: string;
}

export const FormInput = forwardRef(
  (
    {
      name,
      type = "text",
      label,
      isSimpleLabel = false,
      disabled = false,
      height = "medium",
      icon,
      error,
      helpText,
      ...inputProps
    }: InputProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const [passwordIcon, setPasswordIcon] = useState(<EyeOffSvg />);
    let heightStyles;
    switch (height) {
      case "small":
        heightStyles = "h-12";
        break;
      case "medium":
      default:
        heightStyles = "h-14";
        break;
    }

    const showPassword = () => {
      const input = document.getElementById(`${name}__input`);
      const type = input?.getAttribute("type");
      input?.setAttribute("type", type === "password" ? "text" : "password");
      setPasswordIcon(type === "password" ? <EyeSvg /> : <EyeOffSvg />);
    };

    return (
      <div>
        {isSimpleLabel && (
          <label htmlFor={`${name}__input`} className="font-bold text-black-70">
            {label}
          </label>
        )}
        <div className="relative">
          {type === "phone" ? (
            <InputMask
              {...inputProps}
              ref={ref}
              id={`${name}__input`}
              placeholder=""
              autoComplete="off"
              className={clsx(
                heightStyles,
                "peer flex w-full items-center rounded-lg border border-black-16 bg-white-100 px-4 text-black-100 outline-none hover:border-black-32 disabled:bg-black-4 disabled:text-black-54",
                !!error && "border-red-100",
                !!icon && "pr-14",
              )}
              disabled={disabled}
              mask="_____________"
              replacement={{ _: /^[+\d]{1}[\d]{0,12}$/ }}
            />
          ) : (
            <input
              {...inputProps}
              type={type}
              ref={ref}
              id={`${name}__input`}
              placeholder=""
              autoComplete="off"
              className={clsx(
                heightStyles,
                "peer flex w-full items-center rounded-lg border border-black-16 bg-white-100 px-4 text-black-100 outline-none hover:border-black-32 disabled:bg-black-4 disabled:text-black-54",
                !!error && "border-red-100",
                !!icon && "pr-14",
              )}
              disabled={disabled}
              onWheel={(e) => e.currentTarget.blur()}
            />
          )}
          {icon || type === "password" ? (
            <div
              className={clsx(
                "absolute right-4 top-1/4 pt-1",
                !error && "*:text-black-54",
                !!error && "*:text-red-100",
                type === "password" && "cursor-pointer",
              )}
              onClick={
                (type === "password" && showPassword) || inputProps.onClick
              }
            >
              {type === "password" ? passwordIcon : icon}
            </div>
          ) : null}
          {!isSimpleLabel && (
            <label
              htmlFor={`${name}__input`}
              className={clsx(
                "absolute -top-2 ml-3 bg-white-100 px-1 text-100 text-black-54 duration-300 peer-placeholder-shown:top-[18px] peer-placeholder-shown:text-200 peer-focus:-top-2 peer-focus:text-100",
                !!error && "text-red-100",
              )}
            >
              {label}
            </label>
          )}
        </div>
        {(error || helpText) && (
          <p
            className={clsx(
              "pl-4 pt-1 text-100 text-black-54",
              error && "text-red-100",
            )}
          >
            {error || helpText}
          </p>
        )}
      </div>
    );
  },
);
